import type { ThinkingLevel } from "@caupulican/pi-agent-core";
import type { Transport } from "@caupulican/pi-ai";
import {
	Container,
	getCapabilities,
	getKeybindings,
	Input,
	type SelectItem,
	SelectList,
	type SettingItem,
	SettingsList,
	Spacer,
	Text,
} from "@caupulican/pi-tui";
import { type CostGuardSettings, DEFAULT_ENABLED_COST_GUARD_MAX_TURN_USD } from "../../../core/cost-guard.ts";
import { DEFAULT_WORKER_FLEET_LIMITS } from "../../../core/delegation/worker-fleet-limits.ts";
import { formatHttpIdleTimeoutMs, HTTP_IDLE_TIMEOUT_CHOICES } from "../../../core/http-dispatcher.ts";
import { DEFAULT_AUTO_LEARN_SETTINGS, resolveAutoLearnSettings } from "../../../core/learning/auto-learn-settings.ts";
import type {
	AutoLearnSettings,
	AutonomyMode,
	AutonomySettings,
	ContextCurationSettings,
	ContextPromptEnforcementSettings,
	LearningPolicySettings,
	MemoryRetrievalSettings,
	ModelCapabilitySettings,
	ModelRouterSettings,
	ResearchLaneSettings,
	SelfModificationSettings,
	SettingsScope,
	WarningSettings,
	WorkerDelegationSettings,
} from "../../../core/settings-manager.ts";
import {
	DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE,
	DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE_DELAY_MS,
	DEFAULT_AUTONOMY_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES,
	DEFAULT_AUTONOMY_GOAL_CONTINUE_TURNS,
	DEFAULT_AUTONOMY_MAX_STALL_TURNS,
	DEFAULT_LEARNING_POLICY_AUTO_APPLY_ENABLED,
	DEFAULT_LEARNING_POLICY_AUTO_APPLY_SUPERSESSIONS,
	DEFAULT_LEARNING_POLICY_CONFIDENCE_THRESHOLD,
	DEFAULT_LEARNING_POLICY_ENABLED,
	DEFAULT_LEARNING_POLICY_MIN_OBSERVATIONS,
	DEFAULT_RESEARCH_LANE_ENABLED,
	DEFAULT_RESEARCH_LANE_IDLE_DELAY_MS,
	DEFAULT_RESEARCH_LANE_MAX_RUNS_PER_SESSION,
	DEFAULT_RESEARCH_LANE_MAX_USD,
	DEFAULT_RESEARCH_LANE_MAX_WALL_CLOCK_MS,
	DEFAULT_WORKER_DELEGATION_ENABLED,
	DEFAULT_WORKER_DELEGATION_MAX_CONCURRENT,
	DEFAULT_WORKER_DELEGATION_MAX_USD,
	DEFAULT_WORKER_DELEGATION_MAX_WALL_CLOCK_MS,
	DEFAULT_WORKER_DELEGATION_WRITE_ENABLED,
	MAX_WORKER_DELEGATION_MAX_USD,
} from "../../../core/settings-manager.ts";
import { getSelectListTheme, getSettingsListTheme, theme } from "../theme/theme.ts";
import { THINKING_LEVEL_DESCRIPTIONS } from "../thinking-level-descriptions.ts";
import { DynamicBorder } from "./dynamic-border.ts";
import { keyDisplayText } from "./keybinding-hints.ts";
import { COMPACT_SELECTOR_LIST_LAYOUT, SelectorHeading } from "./selector-list.ts";

const AUTO_LEARN_CUSTOM_MODEL_VALUE = "__custom_auto_learn_model__";
const MODEL_ROUTER_UNSET_MODEL_VALUE = "__unset_model_router_model__";
const MODEL_ROUTER_INHERIT_THINKING_VALUE = "__inherit_model_router_thinking__";
const MODEL_ROUTER_INHERIT_THINKING_LABEL = "(inherit)";

const AUTONOMY_MODES: AutonomyMode[] = ["off", "safe", "balanced", "full"];
const AUTONOMY_GOAL_CONTINUE_TURN_VALUES = ["0", "1", "3", "5", "10", "20"];
const AUTONOMY_MAX_STALL_TURN_VALUES = ["0", "5", "10", "20", "30", "50", "100"];
const AUTONOMY_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTE_VALUES = ["0", "15", "30", "60", "120", "240", "480", "1440"];
const AUTONOMY_GOAL_AUTO_CONTINUE_DELAY_MS_VALUES = ["0", "250", "1000", "5000", "30000", "60000"];

const RESEARCH_LANE_MAX_USD_VALUES = ["0.05", "0.1", "0.25", "0.5", "1", "2"];
const RESEARCH_LANE_MAX_WALL_CLOCK_MS_VALUES = ["0", "30000", "60000", "120000", "300000", "600000"];
const RESEARCH_LANE_IDLE_DELAY_MS_VALUES = ["0", "250", "1000", "5000", "30000", "60000"];
const RESEARCH_LANE_MAX_RUNS_PER_SESSION_VALUES = ["0", "1", "3", "5", "10", "25", "50"];

const CONTEXT_POLICY_PRESERVE_RECENT_MESSAGES_VALUES = ["2", "4", "8", "16", "24", "32"];
const CONTEXT_POLICY_MIN_CHARS_VALUES = ["300", "600", "1200", "2400", "4800"];

const CONTEXT_POLICY_ENFORCEMENT_DEFAULTS = {
	enabled: false,
	preserveRecentMessages: 24,
	minChars: 1200,
} as const;

const CONTEXT_MEMORY_RETRIEVAL_MAX_RESULTS_VALUES = ["1", "3", "5", "10", "20"];

const CONTEXT_MEMORY_RETRIEVAL_DEFAULTS = {
	enabled: true,
	maxResults: 5,
	includeInPrompt: true,
	allowExternalEgress: false,
} as const;

function booleanSettingValue(value: boolean | undefined, defaultValue = false): string {
	return (value ?? defaultValue) ? "true" : "false";
}

function optionalStringValue(value: string | undefined, fallback = "(not set)"): string {
	const trimmed = value?.trim();
	return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

function routerTierValue(
	model: string | undefined,
	thinking: ThinkingLevel | undefined,
	fallback = "(not set)",
): string {
	return `${optionalStringValue(model, fallback)} · thinking ${thinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL}`;
}

function normalizeOptionalString(value: string): string | undefined {
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}

function numberSettingValue(value: number | undefined, defaultValue: number): string {
	return String(value ?? defaultValue);
}

function autoLearnModelValue(settings: AutoLearnSettings): string {
	return optionalStringValue(settings.model, DEFAULT_AUTO_LEARN_SETTINGS.model);
}

function selfModificationSummary(settings: SelfModificationSettings): string {
	if (!(settings.enabled ?? false)) return "disabled";
	const hasPath =
		Boolean(settings.sourcePath?.trim()) ||
		(Array.isArray(settings.sourcePaths) && settings.sourcePaths.some((candidate) => Boolean(candidate?.trim())));
	return hasPath ? "enabled" : "enabled (missing path)";
}

function costGuardSummary(settings: CostGuardSettings): string {
	if (!settings.enabled) return "disabled";
	return `${settings.action} at $${settings.maxTurnUsd.toFixed(2)}/turn`;
}

function autonomyModeValue(settings: AutonomySettings): AutonomyMode {
	return settings.mode && AUTONOMY_MODES.includes(settings.mode) ? settings.mode : "off";
}

function autonomySummary(settings: AutonomySettings): string {
	const mode = autonomyModeValue(settings);
	const turns = settings.goalContinueTurns ?? DEFAULT_AUTONOMY_GOAL_CONTINUE_TURNS;
	const stall = settings.maxStallTurns ?? DEFAULT_AUTONOMY_MAX_STALL_TURNS;
	const autoContinue = settings.goalAutoContinue ?? DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE;
	const modeLabel = mode === "full" ? "standing autonomy" : mode;
	return `${modeLabel}, ${turns === 0 ? "unbounded turns" : `${turns} turns`}, stall ${stall}, auto ${autoContinue ? "on" : "off"}`;
}

function autonomyGoalContinueTurnsValue(settings: AutonomySettings): string {
	return String(settings.goalContinueTurns ?? DEFAULT_AUTONOMY_GOAL_CONTINUE_TURNS);
}

function autonomyMaxStallTurnsValue(settings: AutonomySettings): string {
	return String(settings.maxStallTurns ?? DEFAULT_AUTONOMY_MAX_STALL_TURNS);
}

function autonomyGoalContinueMaxWallClockMinutesValue(settings: AutonomySettings): string {
	return String(settings.goalContinueMaxWallClockMinutes ?? DEFAULT_AUTONOMY_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES);
}

function autonomyGoalAutoContinueValue(settings: AutonomySettings): string {
	return String(settings.goalAutoContinue ?? DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE);
}

function autonomyGoalAutoContinueDelayMsValue(settings: AutonomySettings): string {
	return String(settings.goalAutoContinueDelayMs ?? DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE_DELAY_MS);
}

function researchLaneSummary(settings: ResearchLaneSettings): string {
	if (!(settings.enabled ?? DEFAULT_RESEARCH_LANE_ENABLED)) return "disabled";
	const maxUsd = settings.maxUsd ?? DEFAULT_RESEARCH_LANE_MAX_USD;
	const maxRuns = settings.maxRunsPerSession ?? DEFAULT_RESEARCH_LANE_MAX_RUNS_PER_SESSION;
	return `enabled ($${maxUsd}/pass, ${maxRuns} runs/session)`;
}

function contextCurationSummary(settings: ContextCurationSettings): string {
	if (!settings.enabled) return "disabled";
	return `on · ${settings.model ?? "no model"} · ${settings.maxJobsPerTurn ?? 4} jobs/turn`;
}

function learningPolicySummary(settings: LearningPolicySettings): string {
	if (!(settings.enabled ?? DEFAULT_LEARNING_POLICY_ENABLED)) return "disabled (legacy direct-apply)";
	const autoApply = settings.autoApplyEnabled ?? DEFAULT_LEARNING_POLICY_AUTO_APPLY_ENABLED;
	const confidence = settings.confidenceThreshold ?? DEFAULT_LEARNING_POLICY_CONFIDENCE_THRESHOLD;
	return `on · auto-apply ${autoApply ? "on" : "off"} · conf>=${confidence}`;
}

function modelCapabilitySummary(settings: ModelCapabilitySettings): string {
	const mode = settings.mode ?? "auto";
	return mode === "auto" ? "auto (derived from the model's context window)" : mode;
}

function workerDelegationSummary(settings: WorkerDelegationSettings): string {
	if (!(settings.enabled ?? DEFAULT_WORKER_DELEGATION_ENABLED)) return "disabled";
	const maxUsd = settings.maxUsd ?? DEFAULT_WORKER_DELEGATION_MAX_USD;
	return maxUsd > 0 ? `enabled ($${maxUsd}/worker)` : "enabled (unbounded)";
}

function autoLearnSummary(settings: AutoLearnSettings, autonomyMode: AutonomyMode): string {
	const effective = resolveAutoLearnSettings(autonomyMode, settings);
	return effective.enabled ? `enabled (${effective.model})` : "disabled";
}

function contextPolicyEnforcementSummary(settings: ContextPromptEnforcementSettings): string {
	if (!(settings.enabled ?? CONTEXT_POLICY_ENFORCEMENT_DEFAULTS.enabled)) return "disabled";
	const preserve = settings.preserveRecentMessages ?? CONTEXT_POLICY_ENFORCEMENT_DEFAULTS.preserveRecentMessages;
	const minChars = settings.minChars ?? CONTEXT_POLICY_ENFORCEMENT_DEFAULTS.minChars;
	return `enabled (preserve ${preserve}, min ${minChars} chars)`;
}

function contextMemoryRetrievalSummary(settings: MemoryRetrievalSettings): string {
	const externalEgress = settings.allowExternalEgress ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.allowExternalEgress;
	if (!(settings.enabled ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.enabled)) {
		return `disabled (${externalEgress ? "external consent on" : "external off"})`;
	}
	const maxResults = settings.maxResults ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.maxResults;
	const includeInPrompt = settings.includeInPrompt ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.includeInPrompt;
	return `enabled (max ${maxResults}${includeInPrompt ? ", in prompt" : ""}, ${externalEgress ? "external on" : "local only"})`;
}

function modelRouterSummary(settings: ModelRouterSettings): string {
	const state = settings.enabled ? "enabled" : "disabled";
	const gate = settings.fitnessGate ? "gate on" : "gate off";
	return `${state} · ${gate} · cheap: ${optionalStringValue(settings.cheapModel)} · medium: ${optionalStringValue(settings.mediumModel)} · expensive: ${optionalStringValue(settings.expensiveModel)} · learn: ${optionalStringValue(settings.learningModel, "active")}`;
}

function buildAutoLearnModelOptions(
	settings: AutoLearnSettings,
	configuredModelOptions: SelectItem[] | undefined,
	currentModelPattern: string | undefined,
): SelectItem[] {
	const currentValue = autoLearnModelValue(settings);
	const options: SelectItem[] = [
		{
			value: DEFAULT_AUTO_LEARN_SETTINGS.model,
			label: "active",
			description: currentModelPattern
				? `Use the current session model (${currentModelPattern})`
				: "Use the current session model",
		},
	];
	const seen = new Set(options.map((option) => option.value));

	for (const option of configuredModelOptions ?? []) {
		if (seen.has(option.value)) continue;
		options.push(option);
		seen.add(option.value);
	}

	if (currentValue !== DEFAULT_AUTO_LEARN_SETTINGS.model && !seen.has(currentValue)) {
		options.push({
			value: currentValue,
			label: currentValue,
			description: "Current custom setting",
		});
		seen.add(currentValue);
	}

	options.push({
		value: AUTO_LEARN_CUSTOM_MODEL_VALUE,
		label: "Manual / custom…",
		description: "Type a model pattern not listed above",
	});

	return options;
}

function buildModelRouterRoleModelOptions(options: {
	currentValue: string | undefined;
	configuredModelOptions: SelectItem[] | undefined;
	currentModelPattern: string | undefined;
	includeActive: boolean;
	unsetDescription?: string;
}): SelectItem[] {
	const modelOptions: SelectItem[] = [];
	const seen = new Set<string>();

	if (options.includeActive) {
		modelOptions.push({
			value: DEFAULT_AUTO_LEARN_SETTINGS.model,
			label: "active",
			description: options.currentModelPattern
				? `Use the current session model (${options.currentModelPattern})`
				: "Use the current session model",
		});
		seen.add(DEFAULT_AUTO_LEARN_SETTINGS.model);
	} else {
		modelOptions.push({
			value: MODEL_ROUTER_UNSET_MODEL_VALUE,
			label: "(unset)",
			description: options.unsetDescription ?? "Clear this model setting",
		});
		seen.add(MODEL_ROUTER_UNSET_MODEL_VALUE);
	}

	for (const option of options.configuredModelOptions ?? []) {
		if (seen.has(option.value)) continue;
		modelOptions.push(option);
		seen.add(option.value);
	}

	const currentValue = options.currentValue?.trim();
	if (currentValue && !seen.has(currentValue)) {
		modelOptions.push({
			value: currentValue,
			label: currentValue,
			description: "Current custom setting",
		});
		seen.add(currentValue);
	}

	modelOptions.push({
		value: AUTO_LEARN_CUSTOM_MODEL_VALUE,
		label: "Manual / custom…",
		description: "Type a model pattern not listed above",
	});

	return modelOptions;
}

function buildModelRouterThinkingOptions(
	inheritDescription: string,
	availableLevels: ThinkingLevel[] = Object.keys(THINKING_LEVEL_DESCRIPTIONS) as ThinkingLevel[],
): SelectItem[] {
	return [
		{
			value: MODEL_ROUTER_INHERIT_THINKING_VALUE,
			label: MODEL_ROUTER_INHERIT_THINKING_LABEL,
			description: inheritDescription,
		},
		...availableLevels.map((level) => ({
			value: level,
			label: level,
			description: THINKING_LEVEL_DESCRIPTIONS[level],
		})),
	];
}

export interface SettingsConfig {
	autoCompact: boolean;
	costGuard: CostGuardSettings;
	costGuardScope?: SettingsScope;
	showImages: boolean;
	imageWidthCells: number;
	autoResizeImages: boolean;
	blockImages: boolean;
	enableSkillCommands: boolean;
	steeringMode: "all" | "one-at-a-time";
	followUpMode: "all" | "one-at-a-time";
	transport: Transport;
	httpIdleTimeoutMs: number;
	thinkingLevel: ThinkingLevel;
	availableThinkingLevels: ThinkingLevel[];
	currentTheme: string;
	availableThemes: string[];
	hideThinkingBlock: boolean;
	projectContextFiles: "off" | "on-demand";
	projectContextFilesScope?: SettingsScope;
	collapseChangelog: boolean;
	enableInstallTelemetry: boolean;
	doubleEscapeAction: "fork" | "tree" | "none";
	treeFilterMode: "default" | "no-tools" | "user-only" | "labeled-only" | "all";
	showHardwareCursor: boolean;
	editorPaddingX: number;
	autocompleteMaxVisible: number;
	quietStartup: boolean;
	clearOnShrink: boolean;
	showTerminalProgress: boolean;
	warnings: WarningSettings;
	selfModification: {
		enabled: boolean;
		sourcePath?: string;
		sourcePaths?: string[];
	};
	selfModificationScope?: SettingsScope;
	autonomy: AutonomySettings;
	autonomyScope?: SettingsScope;
	researchLane: ResearchLaneSettings;
	researchLaneScope?: SettingsScope;
	workerDelegation: WorkerDelegationSettings;
	contextCuration: ContextCurationSettings;
	contextCurationScope?: SettingsScope;
	learningPolicy: LearningPolicySettings;
	learningPolicyScope?: SettingsScope;
	modelCapability: ModelCapabilitySettings;
	modelCapabilityScope?: SettingsScope;
	workerDelegationScope?: SettingsScope;
	modelRouter: ModelRouterSettings;
	modelRouterScope?: SettingsScope;
	autoLearn: AutoLearnSettings;
	autoLearnScope?: SettingsScope;
	contextPolicyEnforcement: ContextPromptEnforcementSettings;
	contextPolicyEnforcementScope?: SettingsScope;
	contextMemoryRetrieval: MemoryRetrievalSettings;
	contextMemoryRetrievalScope?: SettingsScope;
	currentModelPattern?: string;
	autoLearnModelOptions?: SelectItem[];
	/** Resolve the concrete thinking surface for a configured model pattern. */
	resolveModelThinkingLevels?: (modelPattern: string | undefined) => ThinkingLevel[] | undefined;
	activeProfileName?: string;
	profileOptions?: SelectItem[];
	externalResourceRoots?: string[];
	trustedResourceRoots?: string[];
}

export interface SettingsCallbacks {
	onAutoCompactChange: (enabled: boolean) => void;
	onCostGuardChange: (settings: CostGuardSettings, scope: SettingsScope) => void;
	onShowImagesChange: (enabled: boolean) => void;
	onImageWidthCellsChange: (width: number) => void;
	onAutoResizeImagesChange: (enabled: boolean) => void;
	onBlockImagesChange: (blocked: boolean) => void;
	onEnableSkillCommandsChange: (enabled: boolean) => void;
	onSteeringModeChange: (mode: "all" | "one-at-a-time") => void;
	onFollowUpModeChange: (mode: "all" | "one-at-a-time") => void;
	onTransportChange: (transport: Transport) => void;
	onHttpIdleTimeoutMsChange: (timeoutMs: number) => void;
	onThinkingLevelChange: (level: ThinkingLevel) => void;
	onThemeChange: (theme: string) => void;
	onThemePreview?: (theme: string) => void;
	onHideThinkingBlockChange: (hidden: boolean) => void;
	onProjectContextFilesChange: (mode: "off" | "on-demand", scope: SettingsScope) => void;
	onCollapseChangelogChange: (collapsed: boolean) => void;
	onEnableInstallTelemetryChange: (enabled: boolean) => void;
	onDoubleEscapeActionChange: (action: "fork" | "tree" | "none") => void;
	onTreeFilterModeChange: (mode: "default" | "no-tools" | "user-only" | "labeled-only" | "all") => void;
	onShowHardwareCursorChange: (enabled: boolean) => void;
	onEditorPaddingXChange: (padding: number) => void;
	onAutocompleteMaxVisibleChange: (maxVisible: number) => void;
	onQuietStartupChange: (enabled: boolean) => void;
	onClearOnShrinkChange: (enabled: boolean) => void;
	onShowTerminalProgressChange: (enabled: boolean) => void;
	onWarningsChange: (warnings: WarningSettings) => void;
	onSelfModificationChange: (settings: SelfModificationSettings, scope: SettingsScope) => void;
	onAutonomyChange: (settings: AutonomySettings, scope: SettingsScope) => void;
	onResearchLaneChange: (settings: ResearchLaneSettings, scope: SettingsScope) => void;
	onWorkerDelegationChange: (settings: WorkerDelegationSettings, scope: SettingsScope) => void;
	onContextCurationChange: (settings: ContextCurationSettings, scope: SettingsScope) => void;
	onLearningPolicyChange: (settings: LearningPolicySettings, scope: SettingsScope) => void;
	onModelCapabilityChange: (settings: ModelCapabilitySettings, scope: SettingsScope) => void;
	onModelRouterChange: (settings: ModelRouterSettings, scope: SettingsScope) => void;
	onAutoLearnChange: (settings: AutoLearnSettings, scope: SettingsScope) => void;
	onContextPolicyEnforcementChange: (settings: ContextPromptEnforcementSettings, scope: SettingsScope) => void;
	onContextMemoryRetrievalChange: (settings: MemoryRetrievalSettings, scope: SettingsScope) => void;
	onResourcesHubAction?: (action: string) => void;
	onCancel: () => void;
}

class TextInputSubmenu extends Container {
	private input: Input;

	constructor(
		title: string,
		description: string,
		currentValue: string,
		onSubmit: (value: string) => void,
		onCancel: () => void,
		emptyHint = "empty clears the setting",
	) {
		super();

		this.addChild(new SelectorHeading(title, description));

		this.input = new Input();
		this.input.setValue(currentValue);
		this.input.focused = true;
		this.input.onSubmit = onSubmit;
		this.input.onEscape = onCancel;
		this.addChild(this.input);

		this.addChild(new Spacer(1));
		this.addChild(new Text(theme.fg("dim", `  Enter to save · Esc to go back · ${emptyHint}`), 0, 0));
	}

	handleInput(data: string): void {
		this.input.handleInput(data);
	}
}

class ModelSelectionSubmenu extends Container {
	private searchInput: Input;
	private selectList: SelectList;
	private customInput: TextInputSubmenu | null = null;

	constructor(
		options: SelectItem[],
		currentValue: string,
		onSelect: (value: string) => void,
		onCancel: () => void,
		labels: {
			title: string;
			description: string;
			customTitle: string;
			customDescription: string;
			customEmptyHint: string;
			customEmptyValue: string | undefined;
		} = {
			title: "Auto Learn Scavenger Model",
			description:
				"Choose active or a model from currently configured subscription/API accounts. Type to filter; choose manual for a custom pattern.",
			customTitle: "Custom Auto Learn Model",
			customDescription: 'Enter "active" or a provider/model pattern like "openai/gpt-5.4".',
			customEmptyHint: 'empty uses "active"',
			customEmptyValue: DEFAULT_AUTO_LEARN_SETTINGS.model,
		},
	) {
		super();

		this.addChild(new SelectorHeading(labels.title, labels.description));

		this.searchInput = new Input();
		this.searchInput.focused = true;
		this.addChild(this.searchInput);
		this.addChild(new Spacer(1));

		this.selectList = new SelectList(
			options,
			Math.min(options.length, 10),
			getSelectListTheme(),
			COMPACT_SELECTOR_LIST_LAYOUT,
		);

		const currentIndex = options.findIndex((option) => option.value === currentValue);
		if (currentIndex !== -1) {
			this.selectList.setSelectedIndex(currentIndex);
		}

		this.selectList.onSelect = (item) => {
			if (item.value === AUTO_LEARN_CUSTOM_MODEL_VALUE) {
				this.customInput = new TextInputSubmenu(
					labels.customTitle,
					labels.customDescription,
					currentValue === labels.customEmptyValue ? "" : currentValue,
					(value) => {
						onSelect(normalizeOptionalString(value) ?? labels.customEmptyValue ?? "");
					},
					() => {
						this.customInput = null;
					},
					labels.customEmptyHint,
				);
				return;
			}
			onSelect(item.value);
		};
		this.selectList.onCancel = onCancel;
		this.addChild(this.selectList);

		this.addChild(new Spacer(1));
		this.addChild(new Text(theme.fg("dim", "  Type to filter · Enter to select · Esc to go back"), 0, 0));
	}

	handleInput(data: string): void {
		if (this.customInput) {
			this.customInput.handleInput(data);
			return;
		}

		const kb = getKeybindings();
		if (kb.matches(data, "tui.select.up") || kb.matches(data, "tui.select.down")) {
			this.selectList.handleInput(data);
			return;
		}
		if (kb.matches(data, "tui.select.confirm") || kb.matches(data, "tui.select.cancel")) {
			this.selectList.handleInput(data);
			return;
		}

		this.searchInput.handleInput(data);
		this.selectList.setFilter(this.searchInput.getValue());
	}

	render(width: number): string[] {
		return this.customInput ? this.customInput.render(width) : super.render(width);
	}

	invalidate(): void {
		super.invalidate();
		this.customInput?.invalidate?.();
	}
}

class SettingsListSubmenu extends Container {
	private settingsList!: SettingsList;

	protected mountSettingsList(
		items: SettingItem[],
		onChange: (id: string, newValue: string) => void,
		onCancel: () => void,
	): void {
		this.settingsList = new SettingsList(
			items,
			Math.min(items.length, 10),
			getSettingsListTheme(),
			onChange,
			onCancel,
		);
		this.addChild(this.settingsList);
	}

	handleInput(data: string): void {
		this.settingsList.handleInput(data);
	}
}

class CostGuardSettingsSubmenu extends SettingsListSubmenu {
	private state: CostGuardSettings;
	private scope: SettingsScope;

	constructor(
		settings: CostGuardSettings,
		onChange: (settings: CostGuardSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = { ...settings };
		this.scope = scope;
		this.mountSettingsList(
			[
				{
					id: "cost-guard-scope",
					label: "Save scope",
					description: "Save this foreground cost guard globally or in the current project's .pi/settings.json",
					currentValue: this.scope,
					values: ["global", "project"],
				},
				{
					id: "cost-guard-enabled",
					label: "Enabled",
					description:
						"Explicit opt-in. A saved threshold alone remains dormant and never limits or labels a turn.",
					currentValue: String(this.state.enabled),
					values: ["false", "true"],
				},
				{
					id: "cost-guard-max-turn-usd",
					label: "Maximum USD per turn",
					description: "Projected foreground plus current-turn background spend; zero keeps the guard inactive",
					currentValue: String(this.state.maxTurnUsd),
					submenu: (_currentValue, done) =>
						new TextInputSubmenu(
							"Foreground Cost Guard Threshold",
							"Enter a nonnegative finite USD projection threshold. The Enabled switch is still mandatory.",
							String(this.state.maxTurnUsd),
							(value) => {
								const maxTurnUsd = Number(value.trim());
								if (!Number.isFinite(maxTurnUsd) || maxTurnUsd < 0) {
									done();
									return;
								}
								this.state = { ...this.state, maxTurnUsd };
								onChange({ ...this.state }, this.scope);
								done(String(maxTurnUsd));
							},
							() => done(),
							"nonnegative finite number; invalid input keeps the current value",
						),
				},
				{
					id: "cost-guard-action",
					label: "Action",
					description: "warn only, or reduce reasoning one supported level for the imminent request",
					currentValue: this.state.action,
					values: ["warn", "downgrade"],
				},
			],
			(id, newValue) => {
				switch (id) {
					case "cost-guard-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "cost-guard-enabled":
						this.state = {
							...this.state,
							enabled: newValue === "true",
							maxTurnUsd:
								newValue === "true" && this.state.maxTurnUsd <= 0
									? DEFAULT_ENABLED_COST_GUARD_MAX_TURN_USD
									: this.state.maxTurnUsd,
						};
						break;
					case "cost-guard-action":
						this.state = { ...this.state, action: newValue === "downgrade" ? "downgrade" : "warn" };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

const PROJECT_CONTEXT_SCOPE_VALUES = ["this-directory", "this-project", "all-projects"] as const;
type ProjectContextScopeValue = (typeof PROJECT_CONTEXT_SCOPE_VALUES)[number];

function projectContextScopeValue(scope: SettingsScope | undefined): ProjectContextScopeValue {
	if (scope === "project") return "this-project";
	if (scope === "global") return "all-projects";
	return "this-directory";
}

function projectContextScopeFromValue(value: string): SettingsScope {
	if (value === "this-project") return "project";
	if (value === "all-projects") return "global";
	return "directoryProfile";
}

function projectContextFilesSummary(mode: "off" | "on-demand", scope: SettingsScope | undefined): string {
	if (mode !== "on-demand") return "global only";
	if (scope === "project") return "this project";
	if (scope === "global") return "all projects";
	return "this directory";
}

class ProjectContextFilesSubmenu extends SettingsListSubmenu {
	private mode: "off" | "on-demand";
	private scope: SettingsScope;

	constructor(
		mode: "off" | "on-demand",
		onChange: (mode: "off" | "on-demand", scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "directoryProfile",
	) {
		super();
		this.mode = mode;
		this.scope = scope;
		this.mountSettingsList(
			[
				{
					id: "project-context-scope",
					label: "Save scope",
					description:
						"this-directory: this folder only (user overlay). this-project: .pi/settings.json. all-projects: every repo.",
					currentValue: projectContextScopeValue(this.scope),
					values: [...PROJECT_CONTEXT_SCOPE_VALUES],
				},
				{
					id: "project-context-load",
					label: "Load",
					description:
						"global-only uses ~/.pi/agent/AGENTS.md. on-demand also lists this project's AGENTS.md for the agent to read.",
					currentValue: this.mode === "on-demand" ? "on-demand" : "global-only",
					values: ["global-only", "on-demand"],
				},
			],
			(id, newValue) => {
				if (id === "project-context-scope") {
					this.scope = projectContextScopeFromValue(newValue);
					onChange(this.mode, this.scope);
					return;
				}
				this.mode = newValue === "on-demand" ? "on-demand" : "off";
				onChange(this.mode, this.scope);
			},
			onCancel,
		);
	}
}

class SelfModificationSettingsSubmenu extends SettingsListSubmenu {
	private state: SelfModificationSettings;
	private scope: SettingsScope;

	constructor(
		settings: SelfModificationSettings,
		onChange: (settings: SelfModificationSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();

		this.state = { ...settings, enabled: settings.enabled ?? false };
		this.scope = scope;

		const items: SettingItem[] = [
			{
				id: "self-modification-scope",
				label: "Save scope",
				description:
					"Save this self-modification configuration globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "self-modification-enabled",
				label: "Enabled",
				description: "Allow agents to modify Pi's own source/harness only when explicitly tasked",
				currentValue: booleanSettingValue(this.state.enabled),
				values: ["true", "false"],
			},
			{
				id: "self-modification-source-path",
				label: "Source path",
				description: "Path to the pi-adaptative source checkout agents may edit for self-evolution",
				currentValue: optionalStringValue(this.state.sourcePath),
				submenu: (_currentValue, done) =>
					new TextInputSubmenu(
						"Pi-adaptative Source Path",
						"Set the source checkout path used by self-evolution guardrails. Empty clears it.",
						this.state.sourcePath ?? "",
						(value) => {
							const sourcePath = normalizeOptionalString(value);
							this.state = { ...this.state, sourcePath };
							onChange({ ...this.state }, this.scope);
							done(optionalStringValue(sourcePath));
						},
						() => done(),
					),
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "self-modification-scope":
						this.scope = newValue as SettingsScope;
						onChange({ ...this.state }, this.scope);
						break;
					case "self-modification-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						onChange({ ...this.state }, this.scope);
						break;
				}
			},
			onCancel,
		);
	}
}

class AutonomySettingsSubmenu extends SettingsListSubmenu {
	private state: AutonomySettings;
	private scope: SettingsScope;

	constructor(
		settings: AutonomySettings,
		onChange: (settings: AutonomySettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = {
			mode: autonomyModeValue(settings),
			maxStallTurns: settings.maxStallTurns ?? DEFAULT_AUTONOMY_MAX_STALL_TURNS,
			goalContinueTurns: settings.goalContinueTurns ?? DEFAULT_AUTONOMY_GOAL_CONTINUE_TURNS,
			goalContinueMaxWallClockMinutes:
				settings.goalContinueMaxWallClockMinutes ?? DEFAULT_AUTONOMY_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTES,
			goalAutoContinue: settings.goalAutoContinue ?? DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE,
			goalAutoContinueDelayMs: settings.goalAutoContinueDelayMs ?? DEFAULT_AUTONOMY_GOAL_AUTO_CONTINUE_DELAY_MS,
		};
		this.scope = scope;

		const items: SettingItem[] = [
			{
				id: "autonomy-scope",
				label: "Save scope",
				description: "Save this autonomy preset globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "autonomy-mode",
				label: "Mode",
				description:
					"One preset for learning and root-session reflection cues: off, safe, balanced, or standing autonomy",
				currentValue: autonomyModeValue(this.state),
				values: AUTONOMY_MODES,
			},
			{
				id: "autonomy-goal-auto-continue",
				label: "Goal auto-continue",
				description: "Continue an active goal whenever Pi becomes idle until a real terminal condition",
				currentValue: autonomyGoalAutoContinueValue(this.state),
				values: ["true", "false"],
			},
			{
				id: "autonomy-goal-continue-turns",
				label: "Goal turns",
				description: "Optional continuation prompt limit; 0 is unbounded like Codex",
				currentValue: autonomyGoalContinueTurnsValue(this.state),
				values: AUTONOMY_GOAL_CONTINUE_TURN_VALUES,
			},
			{
				id: "autonomy-max-stall-turns",
				label: "Goal recovery threshold",
				description:
					"Unchanged rounds before Pi must inspect failure evidence and switch approach without stopping",
				currentValue: autonomyMaxStallTurnsValue(this.state),
				values: AUTONOMY_MAX_STALL_TURN_VALUES,
			},
			{
				id: "autonomy-goal-wall-clock-minutes",
				label: "Goal max minutes",
				description: "Wall-clock budget per goal continuation loop; 0 disables the time budget",
				currentValue: autonomyGoalContinueMaxWallClockMinutesValue(this.state),
				values: AUTONOMY_GOAL_CONTINUE_MAX_WALL_CLOCK_MINUTE_VALUES,
			},
			{
				id: "autonomy-goal-auto-continue-delay-ms",
				label: "Goal delay ms",
				description: "Delay before idle auto-continuation starts; 0 starts immediately after idle",
				currentValue: autonomyGoalAutoContinueDelayMsValue(this.state),
				values: AUTONOMY_GOAL_AUTO_CONTINUE_DELAY_MS_VALUES,
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "autonomy-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "autonomy-mode":
						this.state = { ...this.state, mode: newValue as AutonomyMode };
						break;
					case "autonomy-goal-auto-continue":
						this.state = { ...this.state, goalAutoContinue: newValue === "true" };
						break;
					case "autonomy-goal-continue-turns":
						this.state = { ...this.state, goalContinueTurns: Number(newValue) };
						break;
					case "autonomy-max-stall-turns":
						this.state = { ...this.state, maxStallTurns: Number(newValue) };
						break;
					case "autonomy-goal-wall-clock-minutes":
						this.state = { ...this.state, goalContinueMaxWallClockMinutes: Number(newValue) };
						break;
					case "autonomy-goal-auto-continue-delay-ms":
						this.state = { ...this.state, goalAutoContinueDelayMs: Number(newValue) };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class ResearchLaneSettingsSubmenu extends SettingsListSubmenu {
	private state: ResearchLaneSettings;
	private scope: SettingsScope;

	constructor(
		settings: ResearchLaneSettings,
		onChange: (settings: ResearchLaneSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = {
			...settings,
			enabled: settings.enabled ?? DEFAULT_RESEARCH_LANE_ENABLED,
			maxUsd: settings.maxUsd ?? DEFAULT_RESEARCH_LANE_MAX_USD,
			maxWallClockMs: settings.maxWallClockMs ?? DEFAULT_RESEARCH_LANE_MAX_WALL_CLOCK_MS,
			idleDelayMs: settings.idleDelayMs ?? DEFAULT_RESEARCH_LANE_IDLE_DELAY_MS,
			maxRunsPerSession: settings.maxRunsPerSession ?? DEFAULT_RESEARCH_LANE_MAX_RUNS_PER_SESSION,
		};
		this.scope = scope;

		const items: SettingItem[] = [
			{
				id: "research-lane-scope",
				label: "Save scope",
				description: "Save these research-lane settings globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "research-lane-enabled",
				label: "Enabled",
				description:
					"Run bounded, read-only background research when idle with an active goal (requires autonomy mode on)",
				currentValue: String(this.state.enabled),
				values: ["true", "false"],
			},
			{
				id: "research-lane-max-usd",
				label: "Budget per pass",
				description: "USD budget per research pass; breaches mark the lane budget_exhausted in diagnostics",
				currentValue: String(this.state.maxUsd),
				values: RESEARCH_LANE_MAX_USD_VALUES,
			},
			{
				id: "research-lane-max-wall-clock-ms",
				label: "Max milliseconds",
				description: "Wall-clock budget per research pass; 0 disables the time budget",
				currentValue: String(this.state.maxWallClockMs),
				values: RESEARCH_LANE_MAX_WALL_CLOCK_MS_VALUES,
			},
			{
				id: "research-lane-idle-delay-ms",
				label: "Idle delay ms",
				description: "Delay before idle-triggered research starts; 0 starts immediately after idle",
				currentValue: String(this.state.idleDelayMs),
				values: RESEARCH_LANE_IDLE_DELAY_MS_VALUES,
			},
			{
				id: "research-lane-max-runs-per-session",
				label: "Runs per session",
				description: "Maximum idle-triggered research passes per session; explicit /autonomy research is uncapped",
				currentValue: String(this.state.maxRunsPerSession),
				values: RESEARCH_LANE_MAX_RUNS_PER_SESSION_VALUES,
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "research-lane-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "research-lane-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "research-lane-max-usd":
						this.state = { ...this.state, maxUsd: Number(newValue) };
						break;
					case "research-lane-max-wall-clock-ms":
						this.state = { ...this.state, maxWallClockMs: Number(newValue) };
						break;
					case "research-lane-idle-delay-ms":
						this.state = { ...this.state, idleDelayMs: Number(newValue) };
						break;
					case "research-lane-max-runs-per-session":
						this.state = { ...this.state, maxRunsPerSession: Number(newValue) };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class LearningPolicySettingsSubmenu extends SettingsListSubmenu {
	private state: LearningPolicySettings;
	private scope: SettingsScope;

	constructor(
		settings: LearningPolicySettings,
		onChange: (settings: LearningPolicySettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = { ...settings };
		this.scope = scope;
		const items: SettingItem[] = [
			{
				id: "learning-policy-scope",
				label: "Save scope",
				description: "Save these learning-policy settings globally or in the project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "learning-policy-enabled",
				label: "Enabled",
				description:
					"Gate root-session reflection writes through the learning policy (proposals, audits, rollback). Disabled keeps legacy direct-apply with audit records.",
				currentValue: String(this.state.enabled ?? DEFAULT_LEARNING_POLICY_ENABLED),
				values: ["true", "false"],
			},
			{
				id: "learning-policy-auto-apply",
				label: "Auto-apply",
				description: "Allow eligible writes to auto-apply; otherwise everything becomes a proposal",
				currentValue: String(this.state.autoApplyEnabled ?? DEFAULT_LEARNING_POLICY_AUTO_APPLY_ENABLED),
				values: ["true", "false"],
			},
			{
				id: "learning-policy-auto-apply-supersessions",
				label: "Auto-apply supersessions",
				description:
					"Allow a memory_replace/memory_remove (overwrites or deletes an existing fact) to auto-apply once otherwise eligible; off keeps every supersession a proposal",
				currentValue: String(this.state.autoApplySupersessions ?? DEFAULT_LEARNING_POLICY_AUTO_APPLY_SUPERSESSIONS),
				values: ["true", "false"],
			},
			{
				id: "learning-policy-confidence",
				label: "Confidence threshold",
				description: "Minimum confidence (0-100) before a write may auto-apply; below it, proposal or no-op",
				currentValue: String(this.state.confidenceThreshold ?? DEFAULT_LEARNING_POLICY_CONFIDENCE_THRESHOLD),
				values: ["25", "50", "60", "75", "90"],
			},
			{
				id: "learning-policy-min-observations",
				label: "Min observations",
				description: "Observations required before auto-apply (allowedAutoApplyLayers stays JSON-only)",
				currentValue: String(this.state.minObservations ?? DEFAULT_LEARNING_POLICY_MIN_OBSERVATIONS),
				values: ["1", "2", "3", "5"],
			},
		];
		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "learning-policy-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "learning-policy-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "learning-policy-auto-apply":
						this.state = { ...this.state, autoApplyEnabled: newValue === "true" };
						break;
					case "learning-policy-auto-apply-supersessions":
						this.state = { ...this.state, autoApplySupersessions: newValue === "true" };
						break;
					case "learning-policy-confidence":
						this.state = { ...this.state, confidenceThreshold: Number(newValue) };
						break;
					case "learning-policy-min-observations":
						this.state = { ...this.state, minObservations: Number(newValue) };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class ModelCapabilitySettingsSubmenu extends SettingsListSubmenu {
	private state: ModelCapabilitySettings;
	private scope: SettingsScope;

	constructor(
		settings: ModelCapabilitySettings,
		onChange: (settings: ModelCapabilitySettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = { ...settings };
		this.scope = scope;
		const items: SettingItem[] = [
			{
				id: "model-capability-scope",
				label: "Save scope",
				description: "Save globally or in the project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "model-capability-mode",
				label: "Mode",
				description:
					'"auto" derives the tool surface and lane budgets from the model\'s own context window; "off" disables adaptation; a class name forces it',
				currentValue: this.state.mode ?? "auto",
				values: ["auto", "off", "full", "lean", "minimal", "chat"],
			},
		];
		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "model-capability-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "model-capability-mode":
						this.state = { ...this.state, mode: newValue as ModelCapabilitySettings["mode"] };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class ContextCurationSettingsSubmenu extends SettingsListSubmenu {
	private state: ContextCurationSettings;
	private scope: SettingsScope;

	constructor(
		settings: ContextCurationSettings,
		modelOptions: SelectItem[] | undefined,
		onChange: (settings: ContextCurationSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = {
			...settings,
			enabled: settings.enabled ?? false,
			maxJobsPerTurn: settings.maxJobsPerTurn ?? 4,
		};
		this.scope = scope;

		const modelValues = ["(unset)", ...(modelOptions ?? []).map((option) => option.value).filter(Boolean)];
		const items: SettingItem[] = [
			{
				id: "context-curation-scope",
				label: "Save scope",
				description: "Save these curation settings globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "context-curation-enabled",
				label: "Enabled",
				description:
					"Local brain curator: digests GC-packed stubs and scores stale-chunk relevance. Requires a digest-fit local model (run /fitness <model>).",
				currentValue: String(this.state.enabled),
				values: ["true", "false"],
			},
			{
				id: "context-curation-model",
				label: "Curator model",
				description:
					"Local model that runs curation jobs; refused until it passes the digest fitness probe on this host",
				currentValue: this.state.model ?? "(unset)",
				values: modelValues,
			},
			{
				id: "context-curation-max-jobs",
				label: "Max jobs per turn",
				description: "Curation jobs drained per turn (each is one small local completion)",
				currentValue: String(this.state.maxJobsPerTurn),
				values: ["1", "2", "4", "8", "16"],
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "context-curation-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "context-curation-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "context-curation-model":
						this.state = { ...this.state, model: newValue === "(unset)" ? undefined : newValue };
						break;
					case "context-curation-max-jobs":
						this.state = { ...this.state, maxJobsPerTurn: Number(newValue) };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class WorkerDelegationSettingsSubmenu extends SettingsListSubmenu {
	private state: WorkerDelegationSettings;
	private scope: SettingsScope;

	constructor(
		settings: WorkerDelegationSettings,
		onChange: (settings: WorkerDelegationSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = {
			...settings,
			enabled: settings.enabled ?? DEFAULT_WORKER_DELEGATION_ENABLED,
			maxConcurrent: settings.maxConcurrent ?? DEFAULT_WORKER_DELEGATION_MAX_CONCURRENT,
			maxUsd: settings.maxUsd ?? DEFAULT_WORKER_DELEGATION_MAX_USD,
			maxWallClockMs: settings.maxWallClockMs ?? DEFAULT_WORKER_DELEGATION_MAX_WALL_CLOCK_MS,
		};
		this.scope = scope;

		const items: SettingItem[] = [
			{
				id: "worker-delegation-scope",
				label: "Save scope",
				description: "Save these delegation settings globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "worker-delegation-enabled",
				label: "Enabled",
				description:
					"Allow capable models to dispatch autonomous persistent leaf workers; enabled by default, and explicit false is a hard off-switch",
				currentValue: String(this.state.enabled),
				values: ["true", "false"],
			},
			{
				id: "worker-delegation-max-usd",
				label: "Budget per worker task",
				description: "Cumulative USD budget for one worker task; 0 disables this ceiling",
				currentValue: String(this.state.maxUsd),
				submenu: (_currentValue, done) =>
					new TextInputSubmenu(
						"Worker Task USD Budget",
						`Enter a nonnegative finite number no greater than ${MAX_WORKER_DELEGATION_MAX_USD}. Zero disables the settings-level cost ceiling.`,
						String(this.state.maxUsd),
						(value) => {
							const maxUsd = Number(value.trim());
							if (Number.isFinite(maxUsd) && maxUsd >= 0 && maxUsd <= MAX_WORKER_DELEGATION_MAX_USD) {
								this.state = { ...this.state, maxUsd };
								done(String(maxUsd));
								return;
							}
							done();
						},
						() => done(),
						`number in [0, ${MAX_WORKER_DELEGATION_MAX_USD}]; invalid input keeps the current value`,
					),
			},
			{
				id: "worker-delegation-max-wall-clock-ms",
				label: "Worker task milliseconds",
				description: "Active wall-clock budget for one worker task; 0 disables the time budget",
				currentValue: String(this.state.maxWallClockMs),
				submenu: (_currentValue, done) =>
					new TextInputSubmenu(
						"Worker Task Active Time",
						"Enter active milliseconds per worker task as a nonnegative safe integer. Zero disables the time ceiling.",
						String(this.state.maxWallClockMs),
						(value) => {
							const maxWallClockMs = Number(value.trim());
							if (Number.isSafeInteger(maxWallClockMs) && maxWallClockMs >= 0) {
								this.state = { ...this.state, maxWallClockMs };
								done(String(maxWallClockMs));
								return;
							}
							done();
						},
						() => done(),
						"nonnegative safe integer; invalid input keeps the current value",
					),
			},
			{
				id: "worker-delegation-write-enabled",
				label: "Allow file writes",
				description:
					"Let workers change files through direct write/edit tools; default authority is machine-wide and explicit false revokes it",
				currentValue: String(this.state.writeEnabled ?? DEFAULT_WORKER_DELEGATION_WRITE_ENABLED),
				values: ["true", "false"],
			},
			{
				id: "worker-delegation-max-concurrent",
				label: "Max concurrent workers",
				description: `Global scheduler concurrency for leaf workers; the fleet retains at most ${DEFAULT_WORKER_FLEET_LIMITS.maxAgentsPerSession} logical agents per session`,
				currentValue: String(this.state.maxConcurrent),
				submenu: (_currentValue, done) =>
					new TextInputSubmenu(
						"Global Worker Concurrency",
						`Enter any positive safe integer. This limits running leaf workers; fixed safety ceilings separately bound ${DEFAULT_WORKER_FLEET_LIMITS.maxAgentsPerSession} session agents and ${DEFAULT_WORKER_FLEET_LIMITS.maxQueuedDispatches} queued dispatches.`,
						String(this.state.maxConcurrent),
						(value) => {
							const maxConcurrent = Number(value.trim());
							if (Number.isSafeInteger(maxConcurrent) && maxConcurrent > 0) {
								this.state = { ...this.state, maxConcurrent };
								done(String(maxConcurrent));
								return;
							}
							done();
						},
						() => done(),
						"positive safe integer; invalid input keeps the current value",
					),
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "worker-delegation-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "worker-delegation-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "worker-delegation-max-usd":
						this.state = { ...this.state, maxUsd: Number(newValue) };
						break;
					case "worker-delegation-max-wall-clock-ms":
						this.state = { ...this.state, maxWallClockMs: Number(newValue) };
						break;
					case "worker-delegation-write-enabled":
						this.state = { ...this.state, writeEnabled: newValue === "true" };
						break;
					case "worker-delegation-max-concurrent":
						this.state = { ...this.state, maxConcurrent: Number(newValue) };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class AutoLearnSettingsSubmenu extends SettingsListSubmenu {
	private state: AutoLearnSettings;
	private scope: SettingsScope;

	constructor(
		settings: AutoLearnSettings,
		autonomyMode: AutonomyMode,
		currentModelPattern: string | undefined,
		modelOptions: SelectItem[] | undefined,
		onChange: (settings: AutoLearnSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();

		this.state = { ...settings };
		this.scope = scope;
		const defaults = resolveAutoLearnSettings(autonomyMode, settings);
		const modelDescription = currentModelPattern
			? `Model for explicit/background learning. "active" uses ${currentModelPattern}; configured subscription/API models are listed first. Automatic reflection uses the orchestrator's current session turn.`
			: 'Model for explicit/background learning. Use "active" for the current session model, or choose a configured subscription/API model. Automatic reflection uses the orchestrator\'s current session turn.';
		const selectableModelOptions = buildAutoLearnModelOptions(this.state, modelOptions, currentModelPattern);

		const items: SettingItem[] = [
			{
				id: "auto-learn-scope",
				label: "Save scope",
				description: "Save this Auto Learn configuration globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "auto-learn-enabled",
				label: "Enabled",
				description: "Autonomously trigger background history scavenging for long sessions",
				currentValue: booleanSettingValue(this.state.enabled, defaults.enabled),
				values: ["true", "false"],
			},
			{
				id: "auto-learn-model",
				label: "Scavenger model",
				description: modelDescription,
				currentValue: autoLearnModelValue(this.state),
				submenu: (_currentValue, done) =>
					new ModelSelectionSubmenu(
						selectableModelOptions,
						autoLearnModelValue(this.state),
						(value) => {
							this.state = { ...this.state, model: value };
							onChange({ ...this.state }, this.scope);
							done(autoLearnModelValue(this.state));
						},
						() => done(),
					),
			},
			{
				id: "auto-learn-long-session-messages",
				label: "Message trigger",
				description: "Trigger after this many message entries in the active branch",
				currentValue: numberSettingValue(this.state.longSessionMessages, defaults.longSessionMessages),
				values: ["16", "32", "64", "128", "256"],
			},
			{
				id: "auto-learn-context-percent",
				label: "Context trigger %",
				description: "Trigger when current context usage reaches this percentage",
				currentValue: numberSettingValue(this.state.longSessionContextPercent, defaults.longSessionContextPercent),
				values: ["50", "60", "70", "80", "85", "90"],
			},
			{
				id: "auto-learn-cooldown-minutes",
				label: "Cooldown minutes",
				description: "Per-session-tenant cooldown between learner launches",
				currentValue: numberSettingValue(this.state.cooldownMinutes, defaults.cooldownMinutes),
				values: ["15", "30", "60", "120", "240", "1440"],
			},
			{
				id: "auto-learn-lease-minutes",
				label: "Lease minutes",
				description: "Shared-state lease duration for a running background learner",
				currentValue: numberSettingValue(this.state.leaseMinutes, defaults.leaseMinutes),
				values: ["30", "60", "90", "180"],
			},
			{
				id: "auto-learn-max-concurrent",
				label: "Max learners",
				description: "Maximum running Auto Learn background learners per session tenant",
				currentValue: numberSettingValue(this.state.maxConcurrentLearners, defaults.maxConcurrentLearners),
				values: ["1", "2", "3", "4"],
			},
			{
				id: "auto-learn-apply-high-confidence",
				label: "Apply high confidence",
				description:
					"Allow high-confidence memory candidates to be applied automatically; broader write authority follows autonomy.mode",
				currentValue: booleanSettingValue(this.state.applyHighConfidence, defaults.applyHighConfidence),
				values: ["false", "true"],
			},
			{
				id: "auto-learn-reflection-review",
				label: "Reflection review",
				description:
					"Include one durable root-session reflection cue on eligible external turns; completed-turn signals coalesce into it",
				currentValue: booleanSettingValue(this.state.reflectionReview, defaults.reflectionReview),
				values: ["true", "false"],
			},
			{
				id: "auto-learn-reflection-tool-calls",
				label: "Reflection tool trigger",
				description: "Queue a root-session reflection cue after this many tool calls in one completed turn",
				currentValue: numberSettingValue(this.state.reflectionMinToolCalls, defaults.reflectionMinToolCalls),
				values: ["3", "5", "8", "12"],
			},
			{
				id: "auto-learn-reflection-cooldown",
				label: "Reflection cooldown",
				description: "Per-session-tenant cooldown between automatic root-session reflection cues",
				currentValue: numberSettingValue(this.state.reflectionCooldownMinutes, defaults.reflectionCooldownMinutes),
				values: ["15", "30", "60", "120", "1440"],
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "auto-learn-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "auto-learn-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "auto-learn-long-session-messages":
						this.state = { ...this.state, longSessionMessages: parseInt(newValue, 10) };
						break;
					case "auto-learn-context-percent":
						this.state = { ...this.state, longSessionContextPercent: parseInt(newValue, 10) };
						break;
					case "auto-learn-cooldown-minutes":
						this.state = { ...this.state, cooldownMinutes: parseInt(newValue, 10) };
						break;
					case "auto-learn-lease-minutes":
						this.state = { ...this.state, leaseMinutes: parseInt(newValue, 10) };
						break;
					case "auto-learn-max-concurrent":
						this.state = { ...this.state, maxConcurrentLearners: parseInt(newValue, 10) };
						break;
					case "auto-learn-apply-high-confidence":
						this.state = { ...this.state, applyHighConfidence: newValue === "true" };
						break;
					case "auto-learn-reflection-review":
						this.state = { ...this.state, reflectionReview: newValue === "true" };
						break;
					case "auto-learn-reflection-tool-calls":
						this.state = { ...this.state, reflectionMinToolCalls: parseInt(newValue, 10) };
						break;
					case "auto-learn-reflection-cooldown":
						this.state = { ...this.state, reflectionCooldownMinutes: parseInt(newValue, 10) };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class ContextPolicyEnforcementSettingsSubmenu extends SettingsListSubmenu {
	private state: ContextPromptEnforcementSettings;
	private scope: SettingsScope;

	constructor(
		settings: ContextPromptEnforcementSettings,
		onChange: (settings: ContextPromptEnforcementSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();

		this.state = { ...settings };
		this.scope = scope;

		const items: SettingItem[] = [
			{
				id: "context-policy-scope",
				label: "Save scope",
				description:
					"Save this context/prompt-policy configuration globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "context-policy-enabled",
				label: "Prompt policy enforcement",
				description:
					"Opt-in: stub stale artifact-backed grep/find output in the model-visible prompt only. Never touches transcript/session history. Requires the artifact_retrieve tool to be active this turn (not configurable here -- it's a runtime fact, not a setting).",
				currentValue: booleanSettingValue(this.state.enabled, CONTEXT_POLICY_ENFORCEMENT_DEFAULTS.enabled),
				values: ["false", "true"],
			},
			{
				id: "context-policy-preserve-recent-messages",
				label: "Preserve recent messages",
				description: "Never stub a tool result within this many of the most recent messages",
				currentValue: numberSettingValue(
					this.state.preserveRecentMessages,
					CONTEXT_POLICY_ENFORCEMENT_DEFAULTS.preserveRecentMessages,
				),
				values: CONTEXT_POLICY_PRESERVE_RECENT_MESSAGES_VALUES,
			},
			{
				id: "context-policy-min-chars",
				label: "Minimum chars before stubbing",
				description:
					"Only stub a tool result whose original provider-visible text is at least this many characters",
				currentValue: numberSettingValue(this.state.minChars, CONTEXT_POLICY_ENFORCEMENT_DEFAULTS.minChars),
				values: CONTEXT_POLICY_MIN_CHARS_VALUES,
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "context-policy-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "context-policy-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "context-policy-preserve-recent-messages":
						this.state = { ...this.state, preserveRecentMessages: parseInt(newValue, 10) };
						break;
					case "context-policy-min-chars":
						this.state = { ...this.state, minChars: parseInt(newValue, 10) };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class ContextMemoryRetrievalSettingsSubmenu extends SettingsListSubmenu {
	private state: MemoryRetrievalSettings;
	private scope: SettingsScope;

	constructor(
		settings: MemoryRetrievalSettings,
		onChange: (settings: MemoryRetrievalSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();

		this.state = {
			enabled: settings.enabled ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.enabled,
			maxResults: settings.maxResults ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.maxResults,
			includeInPrompt: settings.includeInPrompt ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.includeInPrompt,
			allowExternalEgress: settings.allowExternalEgress ?? CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.allowExternalEgress,
		};
		this.scope = scope;

		const items: SettingItem[] = [
			{
				id: "context-memory-scope",
				label: "Save scope",
				description:
					"Save this local memory-retrieval configuration globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "context-memory-enabled",
				label: "Local memory retrieval",
				description:
					"Default on: search local Pi memory for relevant evidence. Explicit false is a hard off-switch for both legacy recall and context retrieval. External providers remain blocked by the separate consent below.",
				currentValue: booleanSettingValue(this.state.enabled, CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.enabled),
				values: ["false", "true"],
			},
			{
				id: "context-memory-max-results",
				label: "Max results",
				description: "Maximum number of memory items to retrieve per query (1-20)",
				currentValue: numberSettingValue(this.state.maxResults, CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.maxResults),
				values: CONTEXT_MEMORY_RETRIEVAL_MAX_RESULTS_VALUES,
			},
			{
				id: "context-memory-include-in-prompt",
				label: "Include in prompt",
				description:
					"Default on and budget-gated: surface retrieved memory as bounded, source-labeled untrusted evidence in the model prompt only -- never the transcript or an instruction.",
				currentValue: booleanSettingValue(
					this.state.includeInPrompt,
					CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.includeInPrompt,
				),
				values: ["false", "true"],
			},
			{
				id: "context-memory-allow-external-egress",
				label: "Allow external queries",
				description:
					"Explicit consent to send bounded query text to eligible external memory providers. Default off; secret-like and oversized queries still fail closed, and returned recall is centrally fenced as untrusted data.",
				currentValue: booleanSettingValue(
					this.state.allowExternalEgress,
					CONTEXT_MEMORY_RETRIEVAL_DEFAULTS.allowExternalEgress,
				),
				values: ["false", "true"],
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "context-memory-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "context-memory-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "context-memory-max-results":
						this.state = { ...this.state, maxResults: parseInt(newValue, 10) };
						break;
					case "context-memory-include-in-prompt":
						this.state = { ...this.state, includeInPrompt: newValue === "true" };
						break;
					case "context-memory-allow-external-egress":
						this.state = { ...this.state, allowExternalEgress: newValue === "true" };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

class ModelRouterSettingsSubmenu extends SettingsListSubmenu {
	private state: ModelRouterSettings;
	private scope: SettingsScope;

	constructor(
		settings: ModelRouterSettings,
		currentModelPattern: string | undefined,
		modelOptions: SelectItem[] | undefined,
		resolveModelThinkingLevels: SettingsConfig["resolveModelThinkingLevels"],
		onChange: (settings: ModelRouterSettings, scope: SettingsScope) => void,
		onCancel: () => void,
		scope: SettingsScope = "global",
	) {
		super();
		this.state = {
			...settings,
			enabled: settings.enabled ?? false,
			learningModel: settings.learningModel ?? "active",
		};
		const levelsForModel = (modelPattern: string | undefined): ThinkingLevel[] | undefined =>
			resolveModelThinkingLevels?.(modelPattern ?? currentModelPattern);
		const keepSupportedThinking = (
			level: ThinkingLevel | undefined,
			modelPattern: string | undefined,
		): ThinkingLevel | undefined => {
			const levels = levelsForModel(modelPattern);
			return level && levels && !levels.includes(level) ? undefined : level;
		};
		this.state.cheapThinking = keepSupportedThinking(this.state.cheapThinking, this.state.cheapModel);
		this.state.mediumThinking = keepSupportedThinking(this.state.mediumThinking, this.state.mediumModel);
		this.state.expensiveThinking = keepSupportedThinking(this.state.expensiveThinking, this.state.expensiveModel);
		this.state.executorThinking = keepSupportedThinking(this.state.executorThinking, this.state.executorModel);
		this.state.judgeThinking = keepSupportedThinking(this.state.judgeThinking, this.state.judgeModel);
		this.scope = scope;
		const cheapModelOptions = buildModelRouterRoleModelOptions({
			currentValue: this.state.cheapModel,
			configuredModelOptions: modelOptions,
			currentModelPattern,
			includeActive: false,
			unsetDescription: "Clear cheap routing model; research turns fall back to the active session model",
		});
		const mediumModelOptions = buildModelRouterRoleModelOptions({
			currentValue: this.state.mediumModel,
			configuredModelOptions: modelOptions,
			currentModelPattern,
			includeActive: false,
			unsetDescription: "Clear medium routing model; implementation turns fall back according to router policy",
		});
		const expensiveModelOptions = buildModelRouterRoleModelOptions({
			currentValue: this.state.expensiveModel,
			configuredModelOptions: modelOptions,
			currentModelPattern,
			includeActive: false,
			unsetDescription: "Clear expensive routing model; modify turns fall back to the active session model",
		});
		const judgeModelOptions = buildModelRouterRoleModelOptions({
			currentValue: this.state.judgeModel,
			configuredModelOptions: modelOptions,
			currentModelPattern,
			includeActive: false,
			unsetDescription: "Clear judge model; routing judge falls back to the medium model",
		});
		const executorModelOptions = buildModelRouterRoleModelOptions({
			currentValue: this.state.executorModel,
			configuredModelOptions: modelOptions,
			currentModelPattern,
			includeActive: false,
			unsetDescription: "Clear executor lane model; direct toolkit execution stays disabled",
		});
		const learningModelOptions = buildModelRouterRoleModelOptions({
			currentValue: this.state.learningModel,
			configuredModelOptions: modelOptions,
			currentModelPattern,
			includeActive: true,
		});
		const routedTierThinkingOptions = (modelPattern: string | undefined) =>
			buildModelRouterThinkingOptions(
				"Inherit the session thinking level for this turn, clamped to the routed model",
				levelsForModel(modelPattern),
			);
		const judgeThinkingOptions = () =>
			buildModelRouterThinkingOptions(
				"Use today's default of off for the routing judge's own completion",
				levelsForModel(this.state.judgeModel),
			);
		this.addChild(new Text(theme.bold(theme.fg("accent", "Model Router")), 0, 0));
		this.addChild(new Spacer(1));

		const items: SettingItem[] = [
			{
				id: "model-router-scope",
				label: "Save scope",
				description: "Save this routing configuration globally or in the current project's .pi/settings.json",
				currentValue: this.scope,
				values: ["global", "project"],
			},
			{
				id: "model-router-enabled",
				label: "Enabled",
				description: "Route read-only/research turns to cheap model and modify/tool-heavy turns to expensive model",
				currentValue: booleanSettingValue(this.state.enabled),
				values: ["false", "true"],
			},
			{
				id: "model-router-cheap",
				label: "Cheap model",
				description: "Pick the model for read-only, research, explanation, and question turns",
				currentValue: routerTierValue(this.state.cheapModel, this.state.cheapThinking),
				submenu: (_currentValue, done) =>
					new ModelSelectionSubmenu(
						cheapModelOptions,
						this.state.cheapModel ?? MODEL_ROUTER_UNSET_MODEL_VALUE,
						(value) => {
							const cheapModel = value === MODEL_ROUTER_UNSET_MODEL_VALUE ? undefined : value;
							this.state = {
								...this.state,
								cheapModel,
								cheapThinking: keepSupportedThinking(this.state.cheapThinking, cheapModel),
							};
							onChange({ ...this.state }, this.scope);
							done(routerTierValue(this.state.cheapModel, this.state.cheapThinking));
						},
						() => done(),
						{
							title: "Cheap / Research Model",
							description:
								"Choose from models available through configured subscription/API accounts. Type to filter; manual is fallback.",
							customTitle: "Custom Cheap / Research Model",
							customDescription: "Enter a provider/model pattern from pi --list-models.",
							customEmptyHint: "empty clears the setting",
							customEmptyValue: MODEL_ROUTER_UNSET_MODEL_VALUE,
						},
					),
			},
			{
				id: "model-router-medium",
				label: "Medium model",
				description: "Pick the model for normal scoped implementation, edits, tests, and mechanical refactors",
				currentValue: routerTierValue(this.state.mediumModel, this.state.mediumThinking),
				submenu: (_currentValue, done) =>
					new ModelSelectionSubmenu(
						mediumModelOptions,
						this.state.mediumModel ?? MODEL_ROUTER_UNSET_MODEL_VALUE,
						(value) => {
							const mediumModel = value === MODEL_ROUTER_UNSET_MODEL_VALUE ? undefined : value;
							this.state = {
								...this.state,
								mediumModel,
								mediumThinking: keepSupportedThinking(this.state.mediumThinking, mediumModel),
							};
							onChange({ ...this.state }, this.scope);
							done(routerTierValue(this.state.mediumModel, this.state.mediumThinking));
						},
						() => done(),
						{
							title: "Medium / Implementation Model",
							description:
								"Choose from models available through configured subscription/API accounts. Type to filter; manual is fallback.",
							customTitle: "Custom Medium / Implementation Model",
							customDescription: "Enter a provider/model pattern from pi --list-models.",
							customEmptyHint: "empty clears the setting",
							customEmptyValue: MODEL_ROUTER_UNSET_MODEL_VALUE,
						},
					),
			},
			{
				id: "model-router-expensive",
				label: "Expensive model",
				description: "Pick the model for modify, implementation, and escalated tool-heavy turns",
				currentValue: routerTierValue(this.state.expensiveModel, this.state.expensiveThinking),
				submenu: (_currentValue, done) =>
					new ModelSelectionSubmenu(
						expensiveModelOptions,
						this.state.expensiveModel ?? MODEL_ROUTER_UNSET_MODEL_VALUE,
						(value) => {
							const expensiveModel = value === MODEL_ROUTER_UNSET_MODEL_VALUE ? undefined : value;
							this.state = {
								...this.state,
								expensiveModel,
								expensiveThinking: keepSupportedThinking(this.state.expensiveThinking, expensiveModel),
							};
							onChange({ ...this.state }, this.scope);
							done(routerTierValue(this.state.expensiveModel, this.state.expensiveThinking));
						},
						() => done(),
						{
							title: "Expensive / Modify Model",
							description:
								"Choose from models available through configured subscription/API accounts. Type to filter; manual is fallback.",
							customTitle: "Custom Expensive / Modify Model",
							customDescription: "Enter a provider/model pattern from pi --list-models.",
							customEmptyHint: "empty clears the setting",
							customEmptyValue: MODEL_ROUTER_UNSET_MODEL_VALUE,
						},
					),
			},
			{
				id: "model-router-judge",
				label: "Judge model",
				description: "Pick the routing-judge model; unset falls back to the medium model",
				currentValue: routerTierValue(this.state.judgeModel, this.state.judgeThinking, "medium fallback"),
				submenu: (_currentValue, done) =>
					new ModelSelectionSubmenu(
						judgeModelOptions,
						this.state.judgeModel ?? MODEL_ROUTER_UNSET_MODEL_VALUE,
						(value) => {
							const judgeModel = value === MODEL_ROUTER_UNSET_MODEL_VALUE ? undefined : value;
							this.state = {
								...this.state,
								judgeModel,
								judgeThinking: keepSupportedThinking(this.state.judgeThinking, judgeModel),
							};
							onChange({ ...this.state }, this.scope);
							done(routerTierValue(this.state.judgeModel, this.state.judgeThinking, "medium fallback"));
						},
						() => done(),
						{
							title: "Routing Judge Model",
							description: "Choose from configured models. Type to filter; manual is fallback.",
							customTitle: "Custom Routing Judge Model",
							customDescription: "Enter a provider/model pattern from pi --list-models.",
							customEmptyHint: "empty clears the setting",
							customEmptyValue: MODEL_ROUTER_UNSET_MODEL_VALUE,
						},
					),
			},
			{
				id: "model-router-executor",
				label: "Executor model",
				description: "Pick the local executor lane model; unset disables direct toolkit execution",
				currentValue: routerTierValue(this.state.executorModel, this.state.executorThinking),
				submenu: (_currentValue, done) =>
					new ModelSelectionSubmenu(
						executorModelOptions,
						this.state.executorModel ?? MODEL_ROUTER_UNSET_MODEL_VALUE,
						(value) => {
							const executorModel = value === MODEL_ROUTER_UNSET_MODEL_VALUE ? undefined : value;
							this.state = {
								...this.state,
								executorModel,
								executorThinking: keepSupportedThinking(this.state.executorThinking, executorModel),
							};
							onChange({ ...this.state }, this.scope);
							done(routerTierValue(this.state.executorModel, this.state.executorThinking));
						},
						() => done(),
						{
							title: "Executor Lane Model",
							description: "Choose from configured models. Type to filter; manual is fallback.",
							customTitle: "Custom Executor Lane Model",
							customDescription: "Enter a provider/model pattern from pi --list-models.",
							customEmptyHint: "empty clears the setting",
							customEmptyValue: MODEL_ROUTER_UNSET_MODEL_VALUE,
						},
					),
			},
			{
				id: "model-router-learning",
				label: "Learning model",
				description:
					"Pick the model for explicit/background learning and skill-creator work; automatic reflection uses the orchestrator's current session turn",
				currentValue: optionalStringValue(this.state.learningModel, "active"),
				submenu: (_currentValue, done) =>
					new ModelSelectionSubmenu(
						learningModelOptions,
						this.state.learningModel ?? DEFAULT_AUTO_LEARN_SETTINGS.model,
						(value) => {
							this.state = { ...this.state, learningModel: value };
							onChange({ ...this.state }, this.scope);
							done(optionalStringValue(this.state.learningModel, "active"));
						},
						() => done(),
						{
							title: "Learning Model",
							description:
								"Choose active or a model from configured subscription/API accounts for explicit/background learning. Automatic reflection uses the orchestrator's current session turn. Type to filter; manual is fallback.",
							customTitle: "Custom Learning Model",
							customDescription:
								'Enter "active" or a provider/model pattern for explicit/background learning from pi --list-models.',
							customEmptyHint: 'empty uses "active"',
							customEmptyValue: DEFAULT_AUTO_LEARN_SETTINGS.model,
						},
					),
			},
			{
				id: "model-router-cheap-thinking",
				label: "Cheap thinking",
				description:
					"Thinking-level override for cheap-tier (read-only/research) routed turns; (inherit) keeps today's behavior: the session level, clamped to the routed model",
				currentValue: this.state.cheapThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL,
				submenu: (_currentValue, done) =>
					new SelectSubmenu(
						"Cheap Tier Thinking",
						"Select a thinking-level override for cheap-tier routed turns, or inherit the session level",
						routedTierThinkingOptions(this.state.cheapModel),
						this.state.cheapThinking ?? MODEL_ROUTER_INHERIT_THINKING_VALUE,
						(value) => {
							this.state = {
								...this.state,
								cheapThinking:
									value === MODEL_ROUTER_INHERIT_THINKING_VALUE ? undefined : (value as ThinkingLevel),
							};
							onChange({ ...this.state }, this.scope);
							done(this.state.cheapThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL);
						},
						() => done(),
					),
			},
			{
				id: "model-router-medium-thinking",
				label: "Medium thinking",
				description:
					"Thinking-level override for medium-tier (scoped implementation) routed turns; (inherit) keeps today's behavior: the session level, clamped to the routed model",
				currentValue: this.state.mediumThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL,
				submenu: (_currentValue, done) =>
					new SelectSubmenu(
						"Medium Tier Thinking",
						"Select a thinking-level override for medium-tier routed turns, or inherit the session level",
						routedTierThinkingOptions(this.state.mediumModel),
						this.state.mediumThinking ?? MODEL_ROUTER_INHERIT_THINKING_VALUE,
						(value) => {
							this.state = {
								...this.state,
								mediumThinking:
									value === MODEL_ROUTER_INHERIT_THINKING_VALUE ? undefined : (value as ThinkingLevel),
							};
							onChange({ ...this.state }, this.scope);
							done(this.state.mediumThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL);
						},
						() => done(),
					),
			},
			{
				id: "model-router-expensive-thinking",
				label: "Expensive thinking",
				description:
					"Thinking-level override for expensive-tier (modify/escalated) routed turns; (inherit) keeps today's behavior: the session level, clamped to the routed model",
				currentValue: this.state.expensiveThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL,
				submenu: (_currentValue, done) =>
					new SelectSubmenu(
						"Expensive Tier Thinking",
						"Select a thinking-level override for expensive-tier routed turns, or inherit the session level",
						routedTierThinkingOptions(this.state.expensiveModel),
						this.state.expensiveThinking ?? MODEL_ROUTER_INHERIT_THINKING_VALUE,
						(value) => {
							this.state = {
								...this.state,
								expensiveThinking:
									value === MODEL_ROUTER_INHERIT_THINKING_VALUE ? undefined : (value as ThinkingLevel),
							};
							onChange({ ...this.state }, this.scope);
							done(this.state.expensiveThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL);
						},
						() => done(),
					),
			},
			{
				id: "model-router-executor-thinking",
				label: "Executor thinking",
				description:
					"Thinking-level override for the local executor-direct lane; (inherit) keeps today's behavior: the session level, clamped to the routed model",
				currentValue: this.state.executorThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL,
				submenu: (_currentValue, done) =>
					new SelectSubmenu(
						"Executor Thinking",
						"Select a thinking-level override for the executor-direct lane, or inherit the session level",
						routedTierThinkingOptions(this.state.executorModel),
						this.state.executorThinking ?? MODEL_ROUTER_INHERIT_THINKING_VALUE,
						(value) => {
							this.state = {
								...this.state,
								executorThinking:
									value === MODEL_ROUTER_INHERIT_THINKING_VALUE ? undefined : (value as ThinkingLevel),
							};
							onChange({ ...this.state }, this.scope);
							done(this.state.executorThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL);
						},
						() => done(),
					),
			},
			{
				id: "model-router-judge-thinking",
				label: "Judge thinking",
				description:
					"Thinking-level override for the routing judge's own completion; (inherit) keeps today's default of off",
				currentValue: this.state.judgeThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL,
				submenu: (_currentValue, done) =>
					new SelectSubmenu(
						"Judge Thinking",
						"Select a thinking-level override for the routing judge's own completion, or inherit today's off default",
						judgeThinkingOptions(),
						this.state.judgeThinking ?? MODEL_ROUTER_INHERIT_THINKING_VALUE,
						(value) => {
							this.state = {
								...this.state,
								judgeThinking:
									value === MODEL_ROUTER_INHERIT_THINKING_VALUE ? undefined : (value as ThinkingLevel),
							};
							onChange({ ...this.state }, this.scope);
							done(this.state.judgeThinking ?? MODEL_ROUTER_INHERIT_THINKING_LABEL);
						},
						() => done(),
					),
			},
			{
				id: "model-router-fitness-gate",
				label: "Fitness gate",
				description: "Opt-in subtractive gate: blocks tier models only when their probed router lane failed",
				currentValue: booleanSettingValue(this.state.fitnessGate),
				values: ["false", "true"],
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "model-router-scope":
						this.scope = newValue as SettingsScope;
						break;
					case "model-router-enabled":
						this.state = { ...this.state, enabled: newValue === "true" };
						break;
					case "model-router-fitness-gate":
						this.state = { ...this.state, fitnessGate: newValue === "true" };
						break;
					default:
						return;
				}
				onChange({ ...this.state }, this.scope);
			},
			onCancel,
		);
	}
}

/**
 * A submenu component for selecting from a list of options.
 */
class WarningSettingsSubmenu extends SettingsListSubmenu {
	private state: WarningSettings;

	constructor(warnings: WarningSettings, onChange: (warnings: WarningSettings) => void, onCancel: () => void) {
		super();

		this.state = { ...warnings };

		const items: SettingItem[] = [
			{
				id: "anthropic-extra-usage",
				label: "Anthropic extra usage",
				description: "Warn when Anthropic subscription auth may use paid extra usage",
				currentValue: (this.state.anthropicExtraUsage ?? true) ? "true" : "false",
				values: ["true", "false"],
			},
		];

		this.mountSettingsList(
			items,
			(id, newValue) => {
				switch (id) {
					case "anthropic-extra-usage":
						this.state = { ...this.state, anthropicExtraUsage: newValue === "true" };
						onChange({ ...this.state });
						break;
				}
			},
			onCancel,
		);
	}
}

export class SelectSubmenu extends Container {
	private selectList: SelectList;

	getSelectList(): SelectList {
		return this.selectList;
	}

	constructor(
		title: string,
		description: string,
		options: SelectItem[],
		currentValue: string,
		onSelect: (value: string) => void,
		onCancel: () => void,
		onSelectionChange?: (value: string) => void,
	) {
		super();

		this.addChild(new SelectorHeading(title, description));

		// Select list
		this.selectList = new SelectList(
			options,
			Math.min(options.length, 10),
			getSelectListTheme(),
			COMPACT_SELECTOR_LIST_LAYOUT,
		);

		// Pre-select current value
		const currentIndex = options.findIndex((o) => o.value === currentValue);
		if (currentIndex !== -1) {
			this.selectList.setSelectedIndex(currentIndex);
		}

		this.selectList.onSelect = (item) => {
			onSelect(item.value);
		};

		this.selectList.onCancel = onCancel;

		if (onSelectionChange) {
			this.selectList.onSelectionChange = (item) => {
				onSelectionChange(item.value);
			};
		}

		this.addChild(this.selectList);

		// Hint
		this.addChild(new Spacer(1));
		this.addChild(new Text(theme.fg("dim", "  Enter to select · Esc to go back"), 0, 0));
	}

	handleInput(data: string): void {
		const kb = getKeybindings();
		if (kb.matches(data, "app.interrupt") || kb.matches(data, "app.clear")) {
			this.selectList.onCancel?.();
			return;
		}
		this.selectList.handleInput(data);
	}
}

/**
 * Main settings selector component.
 */
export class SettingsSelectorComponent extends Container {
	private settingsList: SettingsList;

	constructor(config: SettingsConfig, callbacks: SettingsCallbacks) {
		super();

		const supportsImages = getCapabilities().images;
		const followUpKey = keyDisplayText("app.message.followUp");
		let currentCostGuard: CostGuardSettings = { ...config.costGuard };
		let currentWarnings = { ...config.warnings };
		let currentSelfModification: SelfModificationSettings = { ...config.selfModification };
		let currentAutonomy: AutonomySettings = { ...config.autonomy };
		let currentResearchLane: ResearchLaneSettings = { ...config.researchLane };
		let currentContextCuration: ContextCurationSettings = { ...config.contextCuration };
		let currentLearningPolicy: LearningPolicySettings = { ...config.learningPolicy };
		let currentModelCapability: ModelCapabilitySettings = { ...config.modelCapability };
		let currentWorkerDelegation: WorkerDelegationSettings = { ...config.workerDelegation };
		let currentModelRouter: ModelRouterSettings = { ...config.modelRouter };
		let currentAutoLearn: AutoLearnSettings = { ...config.autoLearn };
		let currentContextPolicyEnforcement: ContextPromptEnforcementSettings = { ...config.contextPolicyEnforcement };
		let currentContextMemoryRetrieval: MemoryRetrievalSettings = { ...config.contextMemoryRetrieval };
		const items: SettingItem[] = [
			{
				id: "autocompact",
				label: "Auto-compact",
				description: "Automatically compact context when it gets too large",
				currentValue: config.autoCompact ? "true" : "false",
				values: ["true", "false"],
			},
			{
				id: "cost-guard",
				label: "Foreground cost guard",
				description: "Optional per-turn USD projection warning or reasoning downgrade; disabled by default",
				currentValue: costGuardSummary(currentCostGuard),
				submenu: (_currentValue, done) =>
					new CostGuardSettingsSubmenu(
						currentCostGuard,
						(settings, scope) => {
							currentCostGuard = { ...settings };
							callbacks.onCostGuardChange(settings, scope);
						},
						() => done(costGuardSummary(currentCostGuard)),
						config.costGuardScope ?? "global",
					),
			},
			{
				id: "steering-mode",
				label: "Steering mode",
				description:
					"Enter while streaming queues steering messages. 'one-at-a-time': deliver one, wait for response. 'all': deliver all at once.",
				currentValue: config.steeringMode,
				values: ["one-at-a-time", "all"],
			},
			{
				id: "follow-up-mode",
				label: "Follow-up mode",
				description: `${followUpKey} queues follow-up messages until agent stops. 'one-at-a-time': deliver one, wait for response. 'all': deliver all at once.`,
				currentValue: config.followUpMode,
				values: ["one-at-a-time", "all"],
			},
			{
				id: "transport",
				label: "Transport",
				description: "Preferred transport for providers that support multiple transports",
				currentValue: config.transport,
				values: ["sse", "websocket", "websocket-cached", "auto"],
			},
			{
				id: "http-idle-timeout",
				label: "HTTP idle timeout",
				description:
					"Maximum idle gap while waiting for HTTP headers or body chunks. Disable for local models that pause longer than five minutes.",
				currentValue: formatHttpIdleTimeoutMs(config.httpIdleTimeoutMs),
				values: HTTP_IDLE_TIMEOUT_CHOICES.map((choice) => choice.label),
			},
			{
				id: "hide-thinking",
				label: "Hide thinking",
				description: "Hide thinking blocks in assistant responses",
				currentValue: config.hideThinkingBlock ? "true" : "false",
				values: ["true", "false"],
			},
			{
				id: "project-context-files",
				label: "Project AGENTS.md",
				description:
					"Opt in to listing this directory's AGENTS.md/CLAUDE.md/GEMINI.md. Global ~/.pi/agent files always load.",
				currentValue: projectContextFilesSummary(config.projectContextFiles, config.projectContextFilesScope),
				submenu: (_currentValue, done) =>
					new ProjectContextFilesSubmenu(
						config.projectContextFiles,
						(mode, scope) => {
							config.projectContextFiles = mode;
							config.projectContextFilesScope = scope;
							callbacks.onProjectContextFilesChange(mode, scope);
						},
						() => done(projectContextFilesSummary(config.projectContextFiles, config.projectContextFilesScope)),
						config.projectContextFilesScope ?? "directoryProfile",
					),
			},
			{
				id: "collapse-changelog",
				label: "Collapse changelog",
				description: "Show condensed changelog after updates",
				currentValue: config.collapseChangelog ? "true" : "false",
				values: ["true", "false"],
			},
			{
				id: "quiet-startup",
				label: "Quiet startup",
				description: "Disable verbose printing at startup",
				currentValue: config.quietStartup ? "true" : "false",
				values: ["true", "false"],
			},
			{
				id: "install-telemetry",
				label: "Install telemetry",
				description: "Send an anonymous version/update ping after changelog-detected updates",
				currentValue: config.enableInstallTelemetry ? "true" : "false",
				values: ["true", "false"],
			},
			{
				id: "double-escape-action",
				label: "Double-escape action",
				description: "Action when pressing Escape twice with empty editor",
				currentValue: config.doubleEscapeAction,
				values: ["tree", "fork", "none"],
			},
			{
				id: "tree-filter-mode",
				label: "Tree filter mode",
				description: "Default filter when opening /tree",
				currentValue: config.treeFilterMode,
				values: ["default", "no-tools", "user-only", "labeled-only", "all"],
			},
			{
				id: "self-modification",
				label: "Self modification",
				description: "Enable Pi self-evolution guardrails and configure the editable pi-adaptative source checkout",
				currentValue: selfModificationSummary(currentSelfModification),
				submenu: (_currentValue, done) =>
					new SelfModificationSettingsSubmenu(
						currentSelfModification,
						(settings, scope) => {
							currentSelfModification = { ...settings };
							callbacks.onSelfModificationChange(settings, scope);
						},
						() => done(selfModificationSummary(currentSelfModification)),
						config.selfModificationScope ?? "global",
					),
			},
			{
				id: "autonomy",
				label: "Autonomy",
				description: "Choose one autonomy preset instead of tuning many background-learning knobs",
				currentValue: autonomySummary(currentAutonomy),
				submenu: (_currentValue, done) =>
					new AutonomySettingsSubmenu(
						currentAutonomy,
						(settings, scope) => {
							currentAutonomy = { ...settings };
							callbacks.onAutonomyChange(settings, scope);
						},
						() => done(autonomySummary(currentAutonomy)),
						config.autonomyScope ?? "global",
					),
			},
			{
				id: "research-lane",
				label: "Research Lane",
				description:
					"Opt-in autonomous read-only research: bounded background passes that produce evidence bundles for active goals",
				currentValue: researchLaneSummary(currentResearchLane),
				submenu: (_currentValue, done) =>
					new ResearchLaneSettingsSubmenu(
						currentResearchLane,
						(settings, scope) => {
							currentResearchLane = { ...settings };
							callbacks.onResearchLaneChange(settings, scope);
						},
						() => done(researchLaneSummary(currentResearchLane)),
						config.researchLaneScope ?? "global",
					),
			},
			{
				id: "worker-delegation",
				label: "Worker Delegation",
				description:
					"Default-on provider-neutral leaf workers with inherited authority and optional owner-authored execution presets",
				currentValue: workerDelegationSummary(currentWorkerDelegation),
				submenu: (_currentValue, done) =>
					new WorkerDelegationSettingsSubmenu(
						currentWorkerDelegation,
						(settings, scope) => {
							currentWorkerDelegation = { ...settings };
							callbacks.onWorkerDelegationChange(settings, scope);
						},
						() => done(workerDelegationSummary(currentWorkerDelegation)),
						config.workerDelegationScope ?? "global",
					),
			},
			{
				id: "learning-policy",
				label: "Learning Policy",
				description:
					"Gate reflection-sourced durable writes: proposals, confidence thresholds, audits, and rollback",
				currentValue: learningPolicySummary(currentLearningPolicy),
				submenu: (_currentValue, done) =>
					new LearningPolicySettingsSubmenu(
						currentLearningPolicy,
						(settings, scope) => {
							currentLearningPolicy = { ...settings };
							callbacks.onLearningPolicyChange(settings, scope);
						},
						() => done(learningPolicySummary(currentLearningPolicy)),
						config.learningPolicyScope ?? "global",
					),
			},
			{
				id: "model-capability",
				label: "Model Capability",
				description:
					"How pi adapts the tool surface and lane budgets to small models (derived from the model's own metadata)",
				currentValue: modelCapabilitySummary(currentModelCapability),
				submenu: (_currentValue, done) =>
					new ModelCapabilitySettingsSubmenu(
						currentModelCapability,
						(settings, scope) => {
							currentModelCapability = { ...settings };
							callbacks.onModelCapabilityChange(settings, scope);
						},
						() => done(modelCapabilitySummary(currentModelCapability)),
						config.modelCapabilityScope ?? "global",
					),
			},
			{
				id: "context-curation",
				label: "Context Curation",
				description:
					"Opt-in local brain curator: digests GC-packed context stubs and scores stale-chunk relevance in small local-model jobs",
				currentValue: contextCurationSummary(currentContextCuration),
				submenu: (_currentValue, done) =>
					new ContextCurationSettingsSubmenu(
						currentContextCuration,
						config.autoLearnModelOptions,
						(settings, scope) => {
							currentContextCuration = { ...settings };
							callbacks.onContextCurationChange(settings, scope);
						},
						() => done(contextCurationSummary(currentContextCuration)),
						config.contextCurationScope ?? "global",
					),
			},
			{
				id: "model-router",
				label: "Model Router",
				description:
					"Configure models for cheap research, expensive modify/escalation, and explicit/background learning; automatic reflection uses the orchestrator's current session turn",
				currentValue: modelRouterSummary(currentModelRouter),
				submenu: (_currentValue, done) =>
					new ModelRouterSettingsSubmenu(
						currentModelRouter,
						config.currentModelPattern,
						config.autoLearnModelOptions,
						config.resolveModelThinkingLevels,
						(settings, scope) => {
							currentModelRouter = { ...settings };
							callbacks.onModelRouterChange(settings, scope);
						},
						() => done(modelRouterSummary(currentModelRouter)),
						config.modelRouterScope ?? "global",
					),
			},
			{
				id: "auto-learn",
				label: "Auto Learn Advanced",
				description: "Advanced overrides for autonomous background learning/scavenging",
				currentValue: autoLearnSummary(currentAutoLearn, autonomyModeValue(currentAutonomy)),
				submenu: (_currentValue, done) =>
					new AutoLearnSettingsSubmenu(
						currentAutoLearn,
						autonomyModeValue(currentAutonomy),
						config.currentModelPattern,
						config.autoLearnModelOptions,
						(settings, scope) => {
							currentAutoLearn = { ...settings };
							callbacks.onAutoLearnChange(settings, scope);
						},
						() => done(autoLearnSummary(currentAutoLearn, autonomyModeValue(currentAutonomy))),
						config.autoLearnScope ?? "global",
					),
			},
			{
				id: "context-policy-enforcement",
				label: "Context / Prompt Policy",
				description:
					"Opt-in artifact-stub prompt-policy enforcement: stubs stale artifact-backed tool output in the model-visible prompt only, never the transcript. Requires the artifact_retrieve tool to be active.",
				currentValue: contextPolicyEnforcementSummary(currentContextPolicyEnforcement),
				submenu: (_currentValue, done) =>
					new ContextPolicyEnforcementSettingsSubmenu(
						currentContextPolicyEnforcement,
						(settings, scope) => {
							currentContextPolicyEnforcement = { ...settings };
							callbacks.onContextPolicyEnforcementChange(settings, scope);
						},
						() => done(contextPolicyEnforcementSummary(currentContextPolicyEnforcement)),
						config.contextPolicyEnforcementScope ?? "global",
					),
			},
			{
				id: "context-memory-retrieval",
				label: "Context / Memory Retrieval",
				description:
					"Default-on local safe-auto recall with budgeted prompt evidence. External query egress is a separate visible consent and remains off by default.",
				currentValue: contextMemoryRetrievalSummary(currentContextMemoryRetrieval),
				submenu: (_currentValue, done) =>
					new ContextMemoryRetrievalSettingsSubmenu(
						currentContextMemoryRetrieval,
						(settings, scope) => {
							currentContextMemoryRetrieval = { ...settings };
							callbacks.onContextMemoryRetrievalChange(settings, scope);
						},
						() => done(contextMemoryRetrievalSummary(currentContextMemoryRetrieval)),
						config.contextMemoryRetrievalScope ?? "global",
					),
			},
			{
				id: "warnings",
				label: "Warnings",
				description: "Enable or disable individual warnings",
				currentValue: "configure",
				submenu: (_currentValue, done) =>
					new WarningSettingsSubmenu(
						currentWarnings,
						(warnings) => {
							currentWarnings = warnings;
							callbacks.onWarningsChange(warnings);
						},
						() => done(),
					),
			},
			{
				id: "thinking",
				label: "Thinking level",
				description: "Reasoning depth for the active model; delegation policy is configured independently",
				currentValue: config.thinkingLevel,
				submenu: (currentValue, done) =>
					new SelectSubmenu(
						"Thinking Level",
						"Select the reasoning depth supported by the active model",
						config.availableThinkingLevels.map((level) => ({
							value: level,
							label: level,
							description: THINKING_LEVEL_DESCRIPTIONS[level],
						})),
						currentValue,
						(value) => {
							callbacks.onThinkingLevelChange(value as ThinkingLevel);
							done(value);
						},
						() => done(),
					),
			},
			{
				id: "theme",
				label: "Theme",
				description: "Color theme for the interface",
				currentValue: config.currentTheme,
				submenu: (currentValue, done) =>
					new SelectSubmenu(
						"Theme",
						"Select color theme",
						config.availableThemes.map((t) => ({
							value: t,
							label: t,
						})),
						currentValue,
						(value) => {
							callbacks.onThemeChange(value);
							done(value);
						},
						() => {
							// Restore original theme on cancel
							callbacks.onThemePreview?.(currentValue);
							done();
						},
						(value) => {
							// Preview theme on selection change
							callbacks.onThemePreview?.(value);
						},
					),
			},
		];

		const externalRoots = config.externalResourceRoots ?? [];
		const hasSources = externalRoots.length > 0;
		const hasProfiles = (config.profileOptions ?? []).filter((o) => o.value !== "(none)").length > 0;

		items.push({
			id: "resources",
			label: "Resources",
			description: "Manage profiles/situations, library, and external resource sources.",
			currentValue: config.activeProfileName ?? "(none)",
			submenu: (_currentValue, done) => {
				const options: SelectItem[] = [];

				if (!hasSources && !hasProfiles) {
					options.push({
						value: "nudge-add-source",
						label: "Add your catalog directory →",
						description: "First-run nudge: Add a directory of shared skills, extensions, profiles, and agents.",
					});
				}

				options.push({
					value: "active-profile",
					label: "Profile / Situation",
					description: `Select the active profile/situation. Current: ${config.activeProfileName ?? "(none)"}`,
				});

				options.push({
					value: "manage-library",
					label: "Manage Library",
					description: "Toggle allowed tools/skills/extensions in the active profile/scope.",
				});

				options.push({
					value: "manage-profiles",
					label: "Manage Profiles / Situations",
					description: "Create, delete, or persist profile/situation definitions.",
				});

				options.push({
					value: "sources",
					label: "Sources",
					description: `Manage external resource roots (${externalRoots.length} registered).`,
				});

				return new SelectSubmenu(
					"Resources Hub",
					"Configure profiles/situations and external resource catalogs.",
					options,
					"",
					(value) => {
						done();
						callbacks.onResourcesHubAction?.(value);
					},
					() => done(),
				);
			},
		});

		// Only show image toggle if terminal supports it
		if (supportsImages) {
			// Insert after autocompact
			items.splice(1, 0, {
				id: "show-images",
				label: "Show images",
				description: "Render images inline in terminal",
				currentValue: config.showImages ? "true" : "false",
				values: ["true", "false"],
			});
			items.splice(2, 0, {
				id: "image-width-cells",
				label: "Image width",
				description: "Preferred inline image width in terminal cells",
				currentValue: String(config.imageWidthCells),
				values: ["60", "80", "120"],
			});
		}

		// Image auto-resize toggle (always available, affects both attached and read images)
		items.splice(supportsImages ? 3 : 1, 0, {
			id: "auto-resize-images",
			label: "Auto-resize images",
			description: "Resize large images to 2000x2000 max for better model compatibility",
			currentValue: config.autoResizeImages ? "true" : "false",
			values: ["true", "false"],
		});

		// Block images toggle (always available, insert after auto-resize-images)
		const autoResizeIndex = items.findIndex((item) => item.id === "auto-resize-images");
		items.splice(autoResizeIndex + 1, 0, {
			id: "block-images",
			label: "Block images",
			description: "Prevent images from being sent to LLM providers",
			currentValue: config.blockImages ? "true" : "false",
			values: ["true", "false"],
		});

		// Skill commands toggle (insert after block-images)
		const blockImagesIndex = items.findIndex((item) => item.id === "block-images");
		items.splice(blockImagesIndex + 1, 0, {
			id: "skill-commands",
			label: "Skill commands",
			description: "Register skills as /skill:name commands",
			currentValue: config.enableSkillCommands ? "true" : "false",
			values: ["true", "false"],
		});

		// Hardware cursor toggle (insert after skill-commands)
		const skillCommandsIndex = items.findIndex((item) => item.id === "skill-commands");
		items.splice(skillCommandsIndex + 1, 0, {
			id: "show-hardware-cursor",
			label: "Show hardware cursor",
			description: "Show the terminal cursor while still positioning it for IME support",
			currentValue: config.showHardwareCursor ? "true" : "false",
			values: ["true", "false"],
		});

		// Editor padding toggle (insert after show-hardware-cursor)
		const hardwareCursorIndex = items.findIndex((item) => item.id === "show-hardware-cursor");
		items.splice(hardwareCursorIndex + 1, 0, {
			id: "editor-padding",
			label: "Editor padding",
			description: "Horizontal padding for input editor (0-3)",
			currentValue: String(config.editorPaddingX),
			values: ["0", "1", "2", "3"],
		});

		// Autocomplete max visible toggle (insert after editor-padding)
		const editorPaddingIndex = items.findIndex((item) => item.id === "editor-padding");
		items.splice(editorPaddingIndex + 1, 0, {
			id: "autocomplete-max-visible",
			label: "Autocomplete max items",
			description: "Max visible items in autocomplete dropdown (3-20)",
			currentValue: String(config.autocompleteMaxVisible),
			values: ["3", "5", "7", "10", "15", "20"],
		});

		// Clear on shrink toggle (insert after autocomplete-max-visible)
		const autocompleteIndex = items.findIndex((item) => item.id === "autocomplete-max-visible");
		items.splice(autocompleteIndex + 1, 0, {
			id: "clear-on-shrink",
			label: "Clear on shrink",
			description: "Clear empty rows when content shrinks (may cause flicker)",
			currentValue: config.clearOnShrink ? "true" : "false",
			values: ["true", "false"],
		});

		// Terminal progress toggle (insert after clear-on-shrink)
		const clearOnShrinkIndex = items.findIndex((item) => item.id === "clear-on-shrink");
		items.splice(clearOnShrinkIndex + 1, 0, {
			id: "terminal-progress",
			label: "Terminal progress",
			description: "Show OSC 9;4 progress indicators in the terminal tab bar",
			currentValue: config.showTerminalProgress ? "true" : "false",
			values: ["true", "false"],
		});

		// Add borders
		this.addChild(new DynamicBorder());

		this.settingsList = new SettingsList(
			items,
			10,
			getSettingsListTheme(),
			(id, newValue) => {
				switch (id) {
					case "autocompact":
						callbacks.onAutoCompactChange(newValue === "true");
						break;
					case "show-images":
						callbacks.onShowImagesChange(newValue === "true");
						break;
					case "image-width-cells":
						callbacks.onImageWidthCellsChange(parseInt(newValue, 10));
						break;
					case "auto-resize-images":
						callbacks.onAutoResizeImagesChange(newValue === "true");
						break;
					case "block-images":
						callbacks.onBlockImagesChange(newValue === "true");
						break;
					case "skill-commands":
						callbacks.onEnableSkillCommandsChange(newValue === "true");
						break;
					case "steering-mode":
						callbacks.onSteeringModeChange(newValue as "all" | "one-at-a-time");
						break;
					case "follow-up-mode":
						callbacks.onFollowUpModeChange(newValue as "all" | "one-at-a-time");
						break;
					case "transport":
						callbacks.onTransportChange(newValue as Transport);
						break;
					case "http-idle-timeout": {
						const choice = HTTP_IDLE_TIMEOUT_CHOICES.find((item) => item.label === newValue);
						if (choice) {
							callbacks.onHttpIdleTimeoutMsChange(choice.timeoutMs);
						}
						break;
					}
					case "hide-thinking":
						callbacks.onHideThinkingBlockChange(newValue === "true");
						break;
					case "collapse-changelog":
						callbacks.onCollapseChangelogChange(newValue === "true");
						break;
					case "quiet-startup":
						callbacks.onQuietStartupChange(newValue === "true");
						break;
					case "install-telemetry":
						callbacks.onEnableInstallTelemetryChange(newValue === "true");
						break;
					case "double-escape-action":
						callbacks.onDoubleEscapeActionChange(newValue as "fork" | "tree");
						break;
					case "tree-filter-mode":
						callbacks.onTreeFilterModeChange(
							newValue as "default" | "no-tools" | "user-only" | "labeled-only" | "all",
						);
						break;
					case "show-hardware-cursor":
						callbacks.onShowHardwareCursorChange(newValue === "true");
						break;
					case "editor-padding":
						callbacks.onEditorPaddingXChange(parseInt(newValue, 10));
						break;
					case "autocomplete-max-visible":
						callbacks.onAutocompleteMaxVisibleChange(parseInt(newValue, 10));
						break;
					case "clear-on-shrink":
						callbacks.onClearOnShrinkChange(newValue === "true");
						break;
					case "terminal-progress":
						callbacks.onShowTerminalProgressChange(newValue === "true");
						break;
				}
			},
			callbacks.onCancel,
			{ enableSearch: true },
		);

		this.addChild(this.settingsList);
		this.addChild(new DynamicBorder());
	}

	getSettingsList(): SettingsList {
		return this.settingsList;
	}
}
